import { forwardRef, useImperativeHandle, useState } from "react";
import { GreenTick } from "../../../assets/icons";
import { VerifyCustomerResponse } from "../../../model/charge";
import { ConsumerType } from "../../../model/energy-profile";
import { useLazyVerifyPostpaidMeterQuery, useLazyVerifyPrepaidMeterQuery } from "../../../service/vending-service";
import { CardFrame } from "../card-frame/CardFrame";
import styles from "./VerifyMeterNumberField.module.scss";
import CircularProgressIndicator from "../circular-progress-indicator/CircularProgressIndicator";

export interface VerifyMeterNumberFieldHandle {
	verify: () => Promise<void>;
}

interface Props {
	value: string;
	onChange: (value: string) => void;
	consumerType: ConsumerType;
	utilityId: string;
	title?: string;
	placeholder?: string;
	currencySymbol?: string;
	onVerificationResult: (result: VerifyCustomerResponse | null) => void;
	hideVerifyButton?: boolean;
	headerSectionRightContent?: React.ReactNode;
}

export const VerifyMeterNumberField = forwardRef<VerifyMeterNumberFieldHandle, Props>(
	(
		{
			value,
			onChange,
			consumerType,
			utilityId,
			title = "Meter Number",
			placeholder = "",
			currencySymbol = "",
			onVerificationResult,
			hideVerifyButton = false,
			headerSectionRightContent,
		},
		ref
	) => {
		const [isLoading, setIsLoading] = useState(false);
		const [isLoaded, setIsLoaded] = useState(false);
		const [verifiedMeterNumber, setVerifiedMeterNumber] = useState("");
		const [verifyResult, setVerifyResult] = useState<VerifyCustomerResponse | null>(null);
		const [errorMessage, setErrorMessage] = useState("");

		const [triggerVerifyPrepaid] = useLazyVerifyPrepaidMeterQuery();
		const [triggerVerifyPostpaid] = useLazyVerifyPostpaidMeterQuery();

		const isVerified = verifiedMeterNumber !== "" && value === verifiedMeterNumber;

		const getAddressText = () => {
			if (!verifyResult) return "";
			return verifyResult.address ?? verifyResult.accountName ?? "";
		};

		const handleChange = (newValue: string) => {
			onChange(newValue);
			if (!newValue) {
				setVerifyResult(null);
				setVerifiedMeterNumber("");
				setIsLoaded(false);
				setErrorMessage("");
				onVerificationResult(null);
			}
		};

		const verify = async () => {
			setIsLoading(true);
			setIsLoaded(false);
			setErrorMessage("");

			const payload = { MeterNumber: value, UtilityId: utilityId };

			try {
				const trigger = consumerType === ConsumerType.Prepaid ? triggerVerifyPrepaid : triggerVerifyPostpaid;
				const result = await trigger(payload);

				if (result.data) {
					setVerifyResult(result.data);
					setVerifiedMeterNumber(value);
					onVerificationResult(result.data);
					setErrorMessage("");
				} else if (result.error) {
					const err = result.error as any;
					setVerifyResult(null);
					setVerifiedMeterNumber("");
					onVerificationResult(null);
					setErrorMessage(err?.data?.message ?? err?.data?.title ?? "Verification failed");
				}
			} catch {
				setVerifyResult(null);
				setVerifiedMeterNumber("");
				onVerificationResult(null);
				setErrorMessage("Verification failed");
			}

			setIsLoading(false);
			setIsLoaded(true);
		};

		useImperativeHandle(ref, () => ({ verify }));

		const hasOutstandingBalance = verifyResult?.outstandingBalance != null;

		return (
			<CardFrame headerSectionTitle={title} paddingBottom={0} headerSectionRightContent={headerSectionRightContent}>
				<div className={styles.inputRow}>
					<input className={styles.input} value={value} onChange={e => handleChange(e.target.value)} placeholder={placeholder} type="text" />
					{!hideVerifyButton && !isLoading && !isVerified && value && (
						<button className={styles.verifyBtn} onClick={verify} type="button">
							Verify
						</button>
					)}
				</div>

				{isLoading && (
					<div className={`${styles.statusRow} ${styles.statusLoading}`}>
						<CircularProgressIndicator />
						<span>Verifying Meter Number...</span>
					</div>
				)}

				{!isLoading && isLoaded && isVerified && verifyResult && (
					<div className={`${styles.statusRow} ${styles.statusSuccess}`}>
						<GreenTick />
						<span>{getAddressText()}</span>
					</div>
				)}

				{!isLoading && isLoaded && errorMessage && (
					<div className={`${styles.statusRow} ${styles.statusError}`}>
						<span>{errorMessage}</span>
					</div>
				)}

				{hasOutstandingBalance && (
					<div className={styles.outstandingBanner}>{`Your outstanding balance is -${currencySymbol}${verifyResult!.outstandingBalance}`}</div>
				)}
			</CardFrame>
		);
	}
);
